import { useCallback, useEffect, useState } from 'react';
import { subscribe } from 'valtio';

import { contextualMenuState, MenuData } from '../contextual-menu-state';

type WithoutMenuData = {
  closeMenu: () => void;
};

type WithMenuData = {
  toggleOpenMenu: () => void;
  isMenuOpen: boolean;
  openMenu: () => void;
} & WithoutMenuData;

type UseContextualMenuReturnType<T extends MenuData | null = MenuData> = T extends null
  ? WithoutMenuData
  : WithMenuData;

export const useContextualMenu = <T extends MenuData | null = MenuData>(
  menuData: T | null = null
): UseContextualMenuReturnType<T> => {
  const [isOpen, setIsOpen] = useState<boolean>(() =>
    contextualMenuState.isMenuOpen(menuData)
  );

  const closeMenu = useCallback(() => {
    contextualMenuState.closeMenu();
    setIsOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    if (menuData) {
      contextualMenuState.showMenu(menuData);
      setIsOpen(true);
    }
  }, [menuData]);

  const toggleOpenMenu = useCallback(() => {
    setIsOpen((isOpen) => {
      if (isOpen) {
        contextualMenuState.closeMenu();
        return false;
      } else {
        menuData && contextualMenuState.showMenu(menuData);
        return true;
      }
    });
  }, [menuData]);

  useEffect(
    () =>
      subscribe(contextualMenuState, () => {
        const isAnyContextualMenuNotOpen = !contextualMenuState.isMenuOpen(menuData);

        if (isAnyContextualMenuNotOpen && isOpen) {
          setIsOpen(false);
        } else if (!isAnyContextualMenuNotOpen && !isOpen) {
          setIsOpen(true);
        }
      }),
    [isOpen, menuData]
  );

  return {
    closeMenu,
    toggleOpenMenu,
    isMenuOpen: isOpen,
    openMenu,
  } as UseContextualMenuReturnType<T>;
};
